import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FaBook, FaUpload, FaImage, FaUserEdit, FaFilePdf, FaGlobe,
  FaLink, FaRupeeSign, FaTag, FaCheckCircle, FaTimes,
  FaArrowLeft, FaSave, FaRedo, FaTimesCircle, FaInfoCircle,
  FaShieldAlt, FaGooglePlay, FaExternalLinkAlt, FaPercentage,
  FaFileAlt, FaPlus
} from 'react-icons/fa';

// Firebase imports
import { database, storage } from '../firebase'; // Adjust path if needed
import { ref as dbRef, runTransaction, set, get } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const AdminAddEbook = () => {
  const navigate = useNavigate();
  const { ebookId } = useParams();
  const isEditMode = Boolean(ebookId);

  const [formData, setFormData] = useState({
    bookName: '',
    authorName: 'Dr. Niten Gathge',
    totalPages: '',
    description: '',
    language: 'English',
    bookType: 'Paid',
    bookPrice: '',
    discountType: 'percentage',
    discountValue: '',
    discountedPrice: '',
    playStoreLink: '',
    status: 'Active',
    createdBy: 'Admin User'
  });

  const [coverImage, setCoverImage] = useState(null);
  const [coverPreview, setCoverPreview] = useState('');
  const [pdfFiles, setPdfFiles] = useState({
    english: null,
    marathi: null,
    hindi: null,
    other: null
  });
  const [pdfPreviews, setPdfPreviews] = useState({
    english: '',
    marathi: '',
    hindi: '',
    other: ''
  });

  const [tableOfContents, setTableOfContents] = useState(['']);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEditMode);
  const [successMessage, setSuccessMessage] = useState('');

  const languages = ['English', 'Marathi', 'Hindi', 'Other'];
  const bookTypes = ['Free', 'Paid'];
  const discountTypes = [
    { value: 'percentage', label: 'Percentage (%)' },
    { value: 'amount', label: 'Fixed Amount' },
    { value: 'none', label: 'No Discount' }
  ];
  const statusOptions = ['Active', 'Inactive'];

  useEffect(() => {
    if (isEditMode && ebookId) {
      const fetchEbook = async () => {
        try {
          setLoading(true);
          const ebookRef = dbRef(database, `Dr/eBooks/${ebookId}`);
          const snapshot = await get(ebookRef);

          if (snapshot.exists()) {
            const data = snapshot.val();

            setFormData({
              bookName: data.bookName || '',
              authorName: data.authorName || 'Dr. Niten Gathge',
              totalPages: data.totalPages?.toString() || '',
              description: data.description || '',
              language: data.language || 'English',
              bookType: data.bookType || 'Paid',
              bookPrice: data.bookPrice?.toString() || '',
              discountType: data.discountType || 'percentage',
              discountValue: data.discountValue?.toString() || '',
              discountedPrice: data.discountedPrice?.toString() || '',
              playStoreLink: data.playStoreLink || '',
              status: data.status || 'Active',
              createdBy: data.createdBy || 'Admin User'
            });

            setCoverPreview(data.coverUrl);

            const pdfNames = {};
            const pdfUrls = {};
            Object.keys(data.pdfUrls || {}).forEach(lang => {
              pdfNames[lang] = `${lang}.pdf`;
              pdfUrls[lang] = data.pdfUrls[lang];
            });
            setPdfPreviews(pdfNames);

            setTableOfContents(data.tableOfContents?.length > 0 ? data.tableOfContents : ['']);
          } else {
            setErrors({ submit: 'E-Book not found' });
            setTimeout(() => navigate('/admin/ebooks'), 2000);
          }
        } catch (error) {
          console.error('Error fetching e-book:', error);
          setErrors({ submit: 'Failed to load e-book data' });
        } finally {
          setLoading(false);
        }
      };

      fetchEbook();
    }
  }, [isEditMode, ebookId, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));

    if ((name === 'bookPrice' || name === 'discountValue') && formData.bookType === 'Paid') {
      if (formData.discountType === 'percentage' && formData.bookPrice && formData.discountValue) {
        const price = parseFloat(formData.bookPrice) || 0;
        const discount = parseFloat(value) || 0;
        const discounted = price - (price * discount / 100);
        setFormData(prev => ({ ...prev, discountedPrice: discounted.toFixed(2) }));
      }
    }

    if (name === 'bookType' && value === 'Free') {
      setFormData(prev => ({
        ...prev,
        bookPrice: '',
        discountType: 'none',
        discountValue: '',
        discountedPrice: ''
      }));
    }
  };

  const handleCoverImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, coverImage: 'File size must be < 5MB' }));
      return;
    }

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setErrors(prev => ({ ...prev, coverImage: 'Only JPG/PNG/WebP allowed' }));
      return;
    }

    setCoverImage(file);
    const reader = new FileReader();
    reader.onloadend = () => setCoverPreview(reader.result);
    reader.readAsDataURL(file);

    if (errors.coverImage) setErrors(prev => ({ ...prev, coverImage: '' }));
  };

  const handlePdfUpload = (language, e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 50 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, [`pdf_${language}`]: 'PDF size must be < 50MB' }));
      return;
    }

    if (file.type !== 'application/pdf') {
      setErrors(prev => ({ ...prev, [`pdf_${language}`]: 'Only PDF allowed' }));
      return;
    }

    setPdfFiles(prev => ({ ...prev, [language.toLowerCase()]: file }));
    setPdfPreviews(prev => ({ ...prev, [language.toLowerCase()]: file.name }));

    if (errors[`pdf_${language}`]) setErrors(prev => ({ ...prev, [`pdf_${language}`]: '' }));
  };

  const removeCoverImage = () => {
    setCoverImage(null);
    setCoverPreview('');
  };

  const removePdfFile = (language) => {
    setPdfFiles(prev => ({ ...prev, [language]: null }));
    setPdfPreviews(prev => ({ ...prev, [language]: '' }));
  };

  const addTableOfContentsField = () => setTableOfContents(prev => [...prev, '']);
  const removeTableOfContentsField = (index) => {
    if (tableOfContents.length > 1) {
      setTableOfContents(prev => prev.filter((_, i) => i !== index));
    }
  };
  const handleTableOfContentsChange = (index, value) => {
    const newContents = [...tableOfContents];
    newContents[index] = value;
    setTableOfContents(newContents);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!isEditMode && !coverImage) newErrors.coverImage = 'Cover image is required';
    if (!formData.bookName.trim()) newErrors.bookName = 'Book name is required';
    if (!formData.authorName.trim()) newErrors.authorName = 'Author name is required';
    if (!formData.totalPages || parseInt(formData.totalPages) <= 0) {
      newErrors.totalPages = 'Valid total pages required';
    }
    if (!formData.description.trim()) newErrors.description = 'Description is required';

    if (!isEditMode) {
      const hasPdf = Object.values(pdfFiles).some(file => file !== null);
      if (!hasPdf) newErrors.pdfFiles = 'At least one PDF is required';
    }

    if (formData.bookType === 'Paid' && formData.bookPrice) {
      const price = parseFloat(formData.bookPrice);
      if (price <= 0) newErrors.bookPrice = 'Price must be > 0';

      if (formData.discountType !== 'none' && formData.discountValue) {
        const discount = parseFloat(formData.discountValue);
        if (formData.discountType === 'percentage' && (discount < 0 || discount > 100)) {
          newErrors.discountValue = 'Percentage must be 0-100';
        }
        if (formData.discountType === 'amount' && discount >= price) {
          newErrors.discountValue = 'Discount cannot be ≥ price';
        }
      }
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditMode) {
        const existingEbookRef = dbRef(database, `Dr/eBooks/${ebookId}`);
        const existingSnapshot = await get(existingEbookRef);
        const existingData = existingSnapshot.val();

        let coverUrl = existingData.coverUrl;
        let pdfUrls = { ...existingData.pdfUrls };

        if (coverImage) {
          const coverImageRef = storageRef(storage, `Dr/eBooks/${ebookId}/cover.jpg`);
          await uploadBytes(coverImageRef, coverImage);
          coverUrl = await getDownloadURL(coverImageRef);
        }

        for (const lang in pdfFiles) {
          if (pdfFiles[lang]) {
            const pdfRef = storageRef(storage, `Dr/eBooks/${ebookId}/pdfs/${lang}.pdf`);
            await uploadBytes(pdfRef, pdfFiles[lang]);
            pdfUrls[lang] = await getDownloadURL(pdfRef);
          }
        }

        const ebookData = {
          ebookId: existingData.ebookId,
          bookName: formData.bookName.trim(),
          authorName: formData.authorName.trim(),
          totalPages: parseInt(formData.totalPages),
          description: formData.description.trim(),
          language: formData.language,
          bookType: formData.bookType,
          bookPrice: formData.bookType === 'Paid' ? parseFloat(formData.bookPrice || 0) : 0,
          discountType: formData.discountType,
          discountValue: formData.discountValue ? parseFloat(formData.discountValue) : 0,
          discountedPrice: formData.bookType === 'Paid' ? parseFloat(formData.discountedPrice || 0) : 0,
          playStoreLink: formData.playStoreLink.trim(),
          status: formData.status,
          createdBy: existingData.createdBy,
          createdAt: existingData.createdAt,
          updatedAt: new Date().toISOString(),
          isActive: formData.status === 'Active',
          bookCode: existingData.bookCode,
          coverUrl,
          pdfUrls,
          tableOfContents: tableOfContents.filter(item => item.trim())
        };

        await set(existingEbookRef, ebookData);
        setSuccessMessage('E-Book updated successfully!');
      } else {
        const counterRef = dbRef(database, 'Dr/eBooksCounter');
        const { committed, snapshot } = await runTransaction(counterRef, (current) => {
          return (current || 0) + 1;
        });

        if (!committed) throw new Error('Failed to generate eBook ID');

        const newEbookId = snapshot.val();

        const coverImageRef = storageRef(storage, `Dr/eBooks/${newEbookId}/cover.jpg`);
        await uploadBytes(coverImageRef, coverImage);
        const coverUrl = await getDownloadURL(coverImageRef);

        const pdfUrls = {};
        for (const lang in pdfFiles) {
          if (pdfFiles[lang]) {
            const pdfRef = storageRef(storage, `Dr/eBooks/${newEbookId}/pdfs/${lang}.pdf`);
            await uploadBytes(pdfRef, pdfFiles[lang]);
            pdfUrls[lang] = await getDownloadURL(pdfRef);
          }
        }

        const ebookData = {
          ebookId: newEbookId.toString(),
          bookName: formData.bookName.trim(),
          authorName: formData.authorName.trim(),
          totalPages: parseInt(formData.totalPages),
          description: formData.description.trim(),
          language: formData.language,
          bookType: formData.bookType,
          bookPrice: formData.bookType === 'Paid' ? parseFloat(formData.bookPrice || 0) : 0,
          discountType: formData.discountType,
          discountValue: formData.discountValue ? parseFloat(formData.discountValue) : 0,
          discountedPrice: formData.bookType === 'Paid' ? parseFloat(formData.discountedPrice || 0) : 0,
          playStoreLink: formData.playStoreLink.trim(),
          status: formData.status,
          createdBy: formData.createdBy,
          createdAt: new Date().toISOString(),
          isActive: formData.status === 'Active',
          bookCode: `EBOOK-${newEbookId.toString().padStart(5, '0')}`,
          coverUrl,
          pdfUrls,
          tableOfContents: tableOfContents.filter(item => item.trim())
        };

        const ebookRef = dbRef(database, `Dr/eBooks/${newEbookId}`);
        await set(ebookRef, ebookData);

        setSuccessMessage('E-Book added successfully!');
      }

      setTimeout(() => {
        navigate('/admin/ebooks');
      }, 2000);
    } catch (error) {
      console.error('Error saving e-book:', error);
      setErrors({ submit: `Failed to ${isEditMode ? 'update' : 'save'} e-book. Please try again.` });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      bookName: '',
      authorName: 'Dr. Niten Gathge',
      totalPages: '',
      description: '',
      language: 'English',
      bookType: 'Paid',
      bookPrice: '',
      discountType: 'percentage',
      discountValue: '',
      discountedPrice: '',
      playStoreLink: '',
      status: 'Active',
      createdBy: 'Admin User'
    });
    setCoverImage(null);
    setCoverPreview('');
    setPdfFiles({ english: null, marathi: null, hindi: null, other: null });
    setPdfPreviews({ english: '', marathi: '', hindi: '', other: '' });
    setTableOfContents(['']);
    setErrors({});
    setSuccessMessage('');
  };

  const handleCancel = () => navigate('/admin/ebooks');

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">Loading e-book...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button onClick={handleCancel} className="flex items-center text-teal-600 hover:text-teal-700 font-medium mb-4">
            <FaArrowLeft className="mr-2" /> Back to E-Books
          </button>
          <h1 className="text-3xl font-bold text-gray-800">{isEditMode ? 'Edit E-Book' : 'Add New E-Book'}</h1>
          <p className="text-gray-600 mt-2">{isEditMode ? 'Update e-book details and files' : 'Upload and publish healthcare e-books'}</p>
        </div>

        {/* Messages */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center">
            <FaCheckCircle className="text-green-600 text-xl mr-3" />
            <div>
              <p className="font-bold text-green-800">{successMessage}</p>
              <p className="text-sm text-green-600">Redirecting...</p>
            </div>
          </div>
        )}

        {errors.submit && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center">
            <FaTimesCircle className="text-red-600 text-xl mr-3" />
            <p className="font-bold text-red-800">{errors.submit}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left: Media Upload */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-6">
                <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
                  <FaBook className="mr-3 text-indigo-600" /> Media Upload
                </h2>

                {/* Cover Image */}
                <div className="mb-8">
                  <label className="block font-medium text-gray-700 mb-3">
                    Cover Image {!isEditMode && '*'}
                  </label>
                  {coverPreview ? (
                    <div className="relative">
                      <img src={coverPreview} alt="Cover" className="w-full h-64 object-cover rounded-2xl" />
                      <button type="button" onClick={removeCoverImage}
                        className="absolute top-3 right-3 bg-red-500 text-white p-2 rounded-full hover:bg-red-600">
                        <FaTimes />
                      </button>
                    </div>
                  ) : (
                    <label className="block cursor-pointer">
                      <div className="border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center hover:border-indigo-400">
                        <FaUpload className="mx-auto text-4xl text-indigo-600 mb-4" />
                        <p className="font-medium text-gray-700">Upload Cover</p>
                        <p className="text-sm text-gray-500 mt-2">JPG/PNG/WebP • Max 5MB</p>
                      </div>
                      <input type="file" accept="image/*" onChange={handleCoverImageUpload} className="hidden" />
                    </label>
                  )}
                  {errors.coverImage && <p className="text-red-600 text-sm mt-2">{errors.coverImage}</p>}
                </div>

                {/* PDF Uploads */}
                <div className="mb-6">
                  <label className="block font-medium text-gray-700 mb-3">
                    Upload PDFs {!isEditMode && '*'}
                  </label>
                  <div className="space-y-4">
                    {languages.map(lang => (
                      <div key={lang} className="p-4 bg-gray-50 rounded-xl">
                        <div className="flex justify-between mb-2">
                          <span className="font-medium">{lang} PDF</span>
                          {pdfFiles[lang.toLowerCase()] && (
                            <button type="button" onClick={() => removePdfFile(lang.toLowerCase())}
                              className="text-red-600 text-sm hover:text-red-800">
                              Remove
                            </button>
                          )}
                        </div>
                        {pdfPreviews[lang.toLowerCase()] ? (
                          <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                            <FaFilePdf className="text-red-500 text-xl" />
                            <span className="font-medium truncate">{pdfPreviews[lang.toLowerCase()]}</span>
                          </div>
                        ) : (
                          <label className="block cursor-pointer">
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-indigo-300">
                              <FaFilePdf className="text-gray-400 text-2xl mx-auto mb-2" />
                              <span className="text-sm text-gray-600">Upload {lang} PDF</span>
                            </div>
                            <input type="file" accept=".pdf" onChange={(e) => handlePdfUpload(lang.toLowerCase(), e)} className="hidden" />
                          </label>
                        )}
                        {errors[`pdf_${lang.toLowerCase()}`] && (
                          <p className="text-red-600 text-sm mt-2">{errors[`pdf_${lang.toLowerCase()}`]}</p>
                        )}
                      </div>
                    ))}
                  </div>
                  {errors.pdfFiles && <p className="text-red-600 text-sm mt-2">{errors.pdfFiles}</p>}
                </div>
              </div>
            </div>

            {/* Right: Form Fields */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block font-medium text-gray-700 mb-2">Book Title *</label>
                    <div className="relative">
                      <FaBook className="absolute left-3 top-3.5 text-gray-400" />
                      <input
                        type="text" name="bookName" value={formData.bookName} onChange={handleChange}
                        className={`pl-10 w-full p-3 border rounded-xl focus:ring-2 focus:ring-indigo-500 ${errors.bookName ? 'border-red-300' : 'border-gray-300'}`}
                        placeholder="e.g., Complete Guide to Diabetes Care"
                      />
                    </div>
                    {errors.bookName && <p className="text-red-600 text-sm mt-1">{errors.bookName}</p>}
                  </div>

                  <div>
                    <label className="block font-medium text-gray-700 mb-2">Author Name *</label>
                    <div className="relative">
                      <FaUserEdit className="absolute left-3 top-3.5 text-gray-400" />
                      <input
                        type="text" name="authorName" value={formData.authorName} onChange={handleChange}
                        className={`pl-10 w-full p-3 border rounded-xl focus:ring-2 focus:ring-indigo-500 ${errors.authorName ? 'border-red-300' : 'border-gray-300'}`}
                      />
                    </div>
                    {errors.authorName && <p className="text-red-600 text-sm mt-1">{errors.authorName}</p>}
                  </div>

                  <div>
                    <label className="block font-medium text-gray-700 mb-2">Total Pages *</label>
                    <div className="relative">
                      <FaFileAlt className="absolute left-3 top-3.5 text-gray-400" />
                      <input
                        type="number" name="totalPages" value={formData.totalPages} onChange={handleChange}
                        min="1"
                        className={`pl-10 w-full p-3 border rounded-xl focus:ring-2 focus:ring-indigo-500 ${errors.totalPages ? 'border-red-300' : 'border-gray-300'}`}
                      />
                    </div>
                    {errors.totalPages && <p className="text-red-600 text-sm mt-1">{errors.totalPages}</p>}
                  </div>

                  <div>
                    <label className="block font-medium text-gray-700 mb-2">Language *</label>
                    <select name="language" value={formData.language} onChange={handleChange}
                      className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-indigo-500">
                      {languages.map(l => <option key={l} value={l}>{l}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="block font-medium text-gray-700 mb-2">Book Type *</label>
                    <select name="bookType" value={formData.bookType} onChange={handleChange}
                      className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-indigo-500">
                      {bookTypes.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="block font-medium text-gray-700 mb-2">Status *</label>
                    <select name="status" value={formData.status} onChange={handleChange}
                      className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-indigo-500">
                      {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>

                  {formData.bookType === 'Paid' && (
                    <>
                      <div>
                        <label className="block font-medium text-gray-700 mb-2">Book Price (₹) *</label>
                        <div className="relative">
                          <FaRupeeSign className="absolute left-3 top-3.5 text-gray-400" />
                          <input
                            type="number" name="bookPrice" value={formData.bookPrice} onChange={handleChange}
                            min="0" step="0.01"
                            className={`pl-10 w-full p-3 border rounded-xl focus:ring-2 focus:ring-indigo-500 ${errors.bookPrice ? 'border-red-300' : 'border-gray-300'}`}
                          />
                        </div>
                        {errors.bookPrice && <p className="text-red-600 text-sm mt-1">{errors.bookPrice}</p>}
                      </div>

                      <div>
                        <label className="block font-medium text-gray-700 mb-2">Discount Type</label>
                        <select name="discountType" value={formData.discountType} onChange={handleChange}
                          className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-indigo-500">
                          {discountTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </div>

                      {formData.discountType !== 'none' && (
                        <div>
                          <label className="block font-medium text-gray-700 mb-2">
                            {formData.discountType === 'percentage' ? 'Discount (%)' : 'Discount Amount (₹)'}
                          </label>
                          <div className="relative">
                            {formData.discountType === 'percentage' ? <FaPercentage className="absolute left-3 top-3.5 text-gray-400" /> : <FaRupeeSign className="absolute left-3 top-3.5 text-gray-400" />}
                            <input
                              type="number" name="discountValue" value={formData.discountValue} onChange={handleChange}
                              min="0"
                              className={`pl-10 w-full p-3 border rounded-xl focus:ring-2 focus:ring-indigo-500 ${errors.discountValue ? 'border-red-300' : 'border-gray-300'}`}
                            />
                          </div>
                          {errors.discountValue && <p className="text-red-600 text-sm mt-1">{errors.discountValue}</p>}
                        </div>
                      )}
                    </>
                  )}

                  <div className="md:col-span-2">
                    <label className="block font-medium text-gray-700 mb-2">Description *</label>
                    <textarea
                      name="description" value={formData.description} onChange={handleChange}
                      rows="5"
                      className={`w-full p-4 border rounded-xl focus:ring-2 focus:ring-indigo-500 ${errors.description ? 'border-red-300' : 'border-gray-300'}`}
                      placeholder="Brief overview of the book content, benefits, and target audience..."
                    />
                    {errors.description && <p className="text-red-600 text-sm mt-1">{errors.description}</p>}
                  </div>

                  <div className="md:col-span-2">
                    <div className="flex justify-between mb-3">
                      <label className="block font-medium text-gray-700">Table of Contents (Optional)</label>
                      <button type="button" onClick={addTableOfContentsField}
                        className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 flex items-center">
                        <FaPlus className="mr-1" /> Add Chapter
                      </button>
                    </div>
                    <div className="space-y-3">
                      {tableOfContents.map((content, i) => (
                        <div key={i} className="flex gap-3">
                          <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-indigo-100 text-indigo-700 rounded-full font-medium">
                            {i + 1}
                          </div>
                          <input
                            type="text" value={content}
                            onChange={(e) => handleTableOfContentsChange(i, e.target.value)}
                            className="flex-1 p-3 border rounded-xl focus:ring-2 focus:ring-indigo-500"
                            placeholder={`Chapter ${i + 1}: Title`}
                          />
                          {tableOfContents.length > 1 && (
                            <button type="button" onClick={() => removeTableOfContentsField(i)}
                              className="p-3 text-red-600 hover:bg-red-50 rounded-xl">
                              <FaTimes />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block font-medium text-gray-700 mb-2">Play Store Link (Optional)</label>
                    <div className="relative">
                      <FaGooglePlay className="absolute left-3 top-3.5 text-gray-400" />
                      <input
                        type="url" name="playStoreLink" value={formData.playStoreLink} onChange={handleChange}
                        className="pl-10 w-full p-3 border rounded-xl focus:ring-2 focus:ring-indigo-500"
                        placeholder="https://play.google.com/store/apps/..."
                      />
                    </div>
                  </div>

                </div>

                {/* Buttons */}
                <div className="mt-10 pt-6 border-t border-gray-200 flex flex-col sm:flex-row justify-between gap-4">
                  <div className="flex gap-3">
                    <button type="button" onClick={handleCancel}
                      className="px-6 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 flex items-center">
                      <FaTimes className="mr-2" /> Cancel
                    </button>
                    <button type="button" onClick={handleReset}
                      className="px-6 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 flex items-center">
                      <FaRedo className="mr-2" /> Reset
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`px-8 py-3 rounded-xl font-bold text-white flex items-center justify-center ${
                      isSubmitting ? 'bg-indigo-400 cursor-not-allowed' : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:shadow-lg'
                    }`}
                  >
                    {isEditMode ? (
                      <>{isEditMode ? 'Updating' : 'Saving'} E-Book...</>
                    ) : (
                      <><FaSave className="mr-2" /> {isEditMode ? 'Update' : 'Save'} E-Book</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminAddEbook;